import { CONFIG } from '@/config';
import type {
  Plan,
  ProofOfPlayRecord,
  PresenceRecord,
  DeviceHealth,
} from '@/types';
import { getDeviceKey } from './keystore';

interface FetchOptions extends RequestInit {
  noRetry?: boolean;
  timeoutMs?: number;
  /** Skip request signing — for the pairing handshake, which has no key yet. */
  unauth?: boolean;
}

/**
 * Compute the canonical request string the server expects (must match
 * api/src/auth/signature.util.ts:canonicalRequestString verbatim).
 */
async function canonicalize(
  method: string,
  pathAndQuery: string,
  timestamp: number,
  nonce: string,
  body: Uint8Array,
): Promise<Uint8Array> {
  const bodyHash = await sha256Hex(body);
  const text =
    method.toUpperCase() +
    '\n' +
    pathAndQuery +
    '\n' +
    String(timestamp) +
    '\n' +
    nonce +
    '\n' +
    bodyHash;
  return new TextEncoder().encode(text);
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  // Copy into a fresh ArrayBuffer-backed Uint8Array (lib types reject
  // SharedArrayBuffer-backed sources without a tsconfig relaxation).
  const buf = new Uint8Array(bytes.byteLength);
  buf.set(bytes);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const arr = new Uint8Array(digest);
  let s = '';
  for (let i = 0; i < arr.length; i++) s += arr[i].toString(16).padStart(2, '0');
  return s;
}

function randomNonceBase64(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/**
 * The path the server canonicalizes for signature verification is its
 * `req.originalUrl` — i.e. the full on-the-wire path INCLUDING the
 * `/api/v1` prefix (and any query string), but NOT the host. We must
 * sign exactly that, or the signature won't match.
 *
 * `${API_BASE_URL}${path}` is the wire URL. If API_BASE_URL is relative
 * ('/api/v1') it's already a path; if it's absolute (a full URL) we
 * strip the origin and keep pathname+search.
 */
function signingPath(path: string): string {
  const full = `${CONFIG.API_BASE_URL}${path}`;
  try {
    const u = new URL(full);
    return u.pathname + u.search;
  } catch {
    return full; // relative — already the path the server sees
  }
}

async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { noRetry, timeoutMs = 10000, unauth, ...fetchOptions } = options;
  const baseHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string> || {}),
  };

  // Body bytes are stable across attempts; precompute once. The signature
  // (ts/nonce) however MUST be regenerated per attempt — see comment
  // inside the loop.
  const bodyStr =
    typeof fetchOptions.body === 'string'
      ? fetchOptions.body
      : fetchOptions.body
        ? new TextDecoder().decode(fetchOptions.body as ArrayBuffer)
        : '';
  const bodyBytes = new TextEncoder().encode(bodyStr);

  const maxAttempts = noRetry ? 1 : CONFIG.RETRY_MAX_ATTEMPTS;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const headers = { ...baseHeaders };

    // Sign per-attempt. Reusing the same (timestamp, nonce) across retries
    // looks like a replay to the server: nonces are persisted in
    // signature_nonces with a unique (fingerprint, nonce) constraint, so
    // if attempt N actually reached the server (even partially) the next
    // attempt 401s with "Replay". Regenerating per attempt also keeps the
    // timestamp within the ±5 min skew window on slow connections.
    if (!unauth) {
      const key = await getDeviceKey();
      const ts = Math.floor(Date.now() / 1000);
      const nonce = randomNonceBase64();
      const canonical = await canonicalize(
        String(fetchOptions.method ?? 'GET'),
        signingPath(path),
        ts,
        nonce,
        bodyBytes,
      );
      const signature = await key.sign(canonical);
      headers['X-Sensar-Fingerprint'] = key.fingerprint;
      headers['X-Sensar-Timestamp'] = String(ts);
      headers['X-Sensar-Nonce'] = nonce;
      headers['X-Sensar-Signature'] = signature;
    }

    try {
      const res = await fetch(`${CONFIG.API_BASE_URL}${path}`, {
        ...fetchOptions,
        headers,
        signal: AbortSignal.timeout(timeoutMs),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text}`);
      }
      const text = await res.text();
      return text ? JSON.parse(text) : (undefined as unknown as T);
    } catch (err) {
      lastError = err as Error;
      // Retry on transient codes: 408 Timeout, 425 Too Early, 429 Too Many,
      // and 5xx. Other 4xx are caller bugs — no point retrying.
      const msg = lastError.message;
      const isRetriable4xx =
        msg.startsWith('API 408') || msg.startsWith('API 425') || msg.startsWith('API 429');
      const isOtherClientError =
        msg.startsWith('API 4') && !isRetriable4xx;
      if (isOtherClientError) throw lastError;
      if (noRetry) throw lastError;
      // Exponential backoff for network/transient errors, capped so a
      // flapping API doesn't park us in a 5-minute sleep. Jitter prevents
      // a thundering herd when a multi-TV fleet retries after an outage.
      const base = Math.min(
        CONFIG.RETRY_BACKOFF_BASE_MS * Math.pow(2, attempt),
        CONFIG.RETRY_BACKOFF_CAP_MS,
      );
      const jitter = 1 + (Math.random() * 2 - 1) * CONFIG.RETRY_BACKOFF_JITTER;
      const delay = Math.max(0, Math.round(base * jitter));
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastError!;
}

// ── Pairing handshake (unauth — see SECURITY.md) ─────────

/**
 * Register pairing code + this device's public key. Called once on
 * first boot. Server stores publicKeyDer + fingerprint until operator
 * pairs the code with a Board in the dashboard.
 */
export async function registerPairingCode(args: {
  pairingCode: string;
  publicKeyDer: string;
  fingerprint: string;
  keyAlg: string;
}): Promise<void> {
  await apiFetch('/boards/pair/register', {
    method: 'POST',
    body: JSON.stringify(args),
    noRetry: true,
    timeoutMs: 5000,
    unauth: true,
  });
}

/**
 * Step 4a: Request an activation challenge. Server returns 32 random
 * bytes that the player must sign with the private key to prove
 * possession.
 */
export async function requestActivationChallenge(
  pairingCode: string,
): Promise<{ challenge: string; challengeExpiresAt: string }> {
  return apiFetch(`/boards/pair/${pairingCode}/activate`, {
    method: 'POST',
    body: '',
    noRetry: true,
    timeoutMs: 8000,
    unauth: true,
  });
}

/**
 * Cheap status probe for the auto-pair loop. Player polls this every
 * few seconds while the QR is shown; once `ready: true` it kicks off
 * the challenge-response sequence above with zero remote-control input
 * needed on the TV.
 */
export async function pollPairingStatus(pairingCode: string): Promise<{
  status: string;
  ready: boolean;
  expired: boolean;
}> {
  return apiFetch(`/boards/pair/${pairingCode}/status`, {
    method: 'GET',
    noRetry: true,
    timeoutMs: 5000,
    unauth: true,
  });
}

/**
 * Step 4b: Send signature. On success the server installs the player's
 * public key on Board.devicePublicKey and returns boardId. From then on
 * every authenticated request is signed via SignatureAuthGuard.
 */
export async function confirmPairing(
  pairingCode: string,
  signature: string,
): Promise<{ boardId: string; boardCode: string }> {
  return apiFetch(`/boards/pair/${pairingCode}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ signature }),
    noRetry: true,
    timeoutMs: 8000,
    unauth: true,
  });
}

// ── Plan (server-driven schedule) ────────────────────────
//
// One small GET. Player compares plan.planVersion against the cached
// one and only does work when it changed. Replaces the legacy
// fetchBoardCampaigns + N×fetchCampaignMedia flow.

export async function fetchPlan(): Promise<Plan> {
  return apiFetch('/players/me/plan', { timeoutMs: 8000 });
}

// ── Media Download (signed) ──────────────────────────────
//
// Bytes endpoint requires DeviceAuth → must carry the same X-Sensar-*
// signature headers as every other authenticated request. Plain
// `fetch(url)` would 401. We build the canonical request string the
// same way apiFetch does, then return the raw response so the caller
// can read it as a blob (we don't try to JSON-parse it).

export async function downloadMediaBlob(mediaId: string): Promise<Blob> {
  // Step 1: signed request for a presigned R2 URL. Goes through apiFetch
  // (same-origin via the dev proxy) so the X-Sensar-* headers are fine.
  const { url } = await apiFetch<{ url: string }>(
    `/media/${mediaId}/download-url`,
    { timeoutMs: 8000 },
  );

  // Step 2: plain header-free GET straight to R2. No custom headers means
  // it's a CORS "simple request" — no preflight, just needs R2's
  // Access-Control-Allow-Origin (which the bucket CORS policy provides).
  // A 302-redirect approach would replay our X-Sensar-* headers onto R2
  // and trip a preflight the bucket can't satisfy — hence this split.
  const res = await fetch(url, { signal: AbortSignal.timeout(120_000) });
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }
  return res.blob();
}

// ── Heartbeat ────────────────────────────────────────────

export async function sendHeartbeat(
  boardId: string,
  softwareVersion: string,
  playerKind?: 'tizen' | 'sssp' | 'web',
): Promise<void> {
  await apiFetch(`/boards/${boardId}/heartbeat`, {
    method: 'POST',
    body: JSON.stringify({ softwareVersion, playerKind }),
  });
}

// ── Device Health ────────────────────────────────────────
//
// Health metrics piggyback on heartbeat instead of a separate endpoint.
// The legacy reportHealth() did PATCH /boards/:id which is JWT-only and
// 401'd silently for the player. Removed entirely; if we want a
// dedicated channel later we'll add one with @DeviceAuth.

export async function sendHealthReport(
  boardId: string,
  health: DeviceHealth,
  playerKind?: 'tizen' | 'sssp' | 'web',
): Promise<void> {
  await apiFetch(`/boards/${boardId}/heartbeat`, {
    method: 'POST',
    body: JSON.stringify({
      softwareVersion: health.softwareVersion,
      playerKind,
    }),
  });
}

// ── Proof of Play ────────────────────────────────────────

export async function uploadProofOfPlay(
  records: Omit<ProofOfPlayRecord, 'id' | 'synced'>[],
): Promise<void> {
  await apiFetch('/analytics/proof-of-play', {
    method: 'POST',
    body: JSON.stringify(records),
  });
}

// ── Presence Data ────────────────────────────────────────

export async function uploadPresenceData(
  records: Omit<PresenceRecord, 'id' | 'synced'>[],
): Promise<void> {
  await apiFetch('/analytics/presence', {
    method: 'POST',
    body: JSON.stringify(records),
  });
}

// ── Verbose Logs ─────────────────────────────────────────
//
// Player buffers log records locally and flushes batches here on a
// 30 s timer. DeviceAuth on the server takes the boardId from the
// signed request — payload carries level, message, optional context,
// and the bundle/shell versions so post-deploy regressions are easy
// to correlate.

interface PlayerLogPayload {
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  context?: Record<string, unknown>;
  timestamp: string;
  bundleVersion?: string;
  shellVersion?: string;
}

export async function uploadPlayerLogs(
  entries: PlayerLogPayload[],
): Promise<{ accepted: number }> {
  return apiFetch<{ accepted: number }>('/players/me/logs', {
    method: 'POST',
    body: JSON.stringify({ entries }),
    // No retry — flush interval will pick up dropped batches.
    noRetry: true,
    timeoutMs: 15_000,
  });
}

// ── Connectivity Check ───────────────────────────────────
//
// Hits the public /health endpoint (no auth, no body, no DB query).
// Was previously hitting /dashboard/stats which became JWT-only — that
// silently 401'd the player into "offline forever" mode. Don't repeat
// that — keep this on a dedicated public health route.

export async function checkConnectivity(): Promise<boolean> {
  try {
    const res = await fetch(`${CONFIG.API_BASE_URL}/health`, {
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch {
    return false;
  }
}
